import "@/App.css";
import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { applyTheme } from "@/lib/theme";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import AddItem from "@/pages/AddItem";
import Inventory from "@/pages/Inventory";
import Threadworth from "@/pages/Threadworth";
import ReviewPage from "@/pages/Review";
import MarketPage from "@/pages/Market";
import Planner from "@/pages/Planner";
import History from "@/pages/History";
import Trips from "@/pages/Trips";
import Social from "@/pages/Social";
import Profile from "@/pages/Profile";
import SettingsPage from "@/pages/Settings";
import AuthPage from "@/pages/Auth";
import LookbookPage from "@/pages/Lookbook";
import WelcomePage from "@/pages/Welcome";
import DemoPage from "@/pages/Demo";

applyTheme();

const Blank = () => <div className="min-h-screen bg-background" />;

/** `/` is the public landing when signed out, the app home when signed in. */
const Root = () => {
  const { user, loading } = useAuth();
  if (loading) return <Blank />;
  return user ? <Navigate to="/home" replace /> : <Landing />;
};

const RequireAuth = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const hadUser = useRef(false);
  if (user) hadUser.current = true;
  if (loading) return <Blank />;
  // signed out from inside the app -> landing; cold deep-link -> login (and come back after)
  if (!user) return hadUser.current ? <Navigate to="/" replace /> : <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Outlet />;
};

/** First sign-in -> onboarding. Cached locally so we don't re-check on every navigation. */
const RequireOnboarded = () => {
  const { user } = useAuth();
  // demo visitors go straight in - the demo closet is a finished closet, not a setup
  const [state, setState] = useState(() => (user?.is_demo || localStorage.getItem(`attire.onboarded.${user?.id}`) === "1" ? "ok" : "checking"));
  useEffect(() => {
    if (state !== "checking") return;
    api.getProfile().then((p) => {
      if (p.onboarded) { localStorage.setItem(`attire.onboarded.${user?.id}`, "1"); setState("ok"); } else setState("welcome");
    }).catch(() => setState("ok"));
  }, [state, user?.id]);
  if (state === "checking") return <Blank />;
  if (state === "welcome") return <Navigate to="/welcome" replace />;
  return <Outlet />;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route element={<RequireAuth />}>
              <Route path="/welcome" element={<WelcomePage />} />
              <Route element={<RequireOnboarded />}>
                <Route element={<Layout />}>
                  <Route path="/home" element={<Dashboard />} />
                  <Route path="/add" element={<AddItem />} />
                  <Route path="/inventory" element={<Inventory />} />
                  <Route path="/worth" element={<Threadworth />} />
                  <Route path="/review" element={<ReviewPage />} />
                  <Route path="/looks" element={<LookbookPage />} />
                  <Route path="/planner" element={<Planner />} />
                  <Route path="/history" element={<History />} />
                  <Route path="/trips" element={<Trips />} />
                  <Route path="/social" element={<Social />} />
                  <Route path="/market" element={<MarketPage />} />
                  <Route path="/u/:handle" element={<Profile />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Route>
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-center" theme="light" toastOptions={{ classNames: { toast: "group toast !bg-background !text-foreground !border-border !rounded-2xl !shadow-lift", success: "[&_svg]:!text-good", error: "[&_svg]:!text-bad", info: "[&_svg]:!text-info", warning: "[&_svg]:!text-wait" } }} />
    </div>
  );
}

export default App;
